import { importProvidersFrom } from '@angular/core';
import { isAuthorizedUserGuard } from '@core/authorization/guards/is-authorized-user.guard';
import { isMigratedUserGuard } from '@core/authorization/guards/is-migrated-user.guard';
import { isNotMigratedUserGuard } from '@core/authorization/guards/is-not-migrated-user.guard';
import { redirectMigratedUserGuard } from '@core/authorization/guards/redirect-migrated-user.guard';
import { ApplicationPath } from '@core/navigation/models/application-path.enum';
import { hasLoginContextGuard } from '@core/authorization/guards/has-login-context.guard';
import { isFeatureEnabledGuard } from '@core/features/guards/is-feature-enabled.guard';
import { Feature, Permission } from '@kaseya-shared';
import { redirectToFn } from '@core/navigation/helpers/redirect-to';
import { TopNavigationPath } from '@core/navigation/models/top-navigation/top-navigation-path';
import { PageTitleTranslationKey } from '@core/page-title/models/page-title-translation-key.enum';
import { hasPermissionGuard } from '@core/permissions/guards/has-permission.guard';
import { AppRoutes } from '@core/route-data/models/app-routes';
import { PostLogoutWorkflow } from '@core/sso/models/post-logout-workflow.enum';
import { provideFeatureTranslations } from '@core/translation/models/feature-translations.provider';
import { TranslationFeature } from '@core/translation/models/translation-feature.enum';
import { AuthViewComponent } from '@layout/auth-view/components/auth-view.component';
import { hasSetupGuidesAccessGuard } from '@layout/k365-setup-guides-access/guards/has-setup-guides-access.guard';
import { MainViewComponent } from '@layout/main-view/components/main-view.component';
import { isPaymentManagementActiveGuard } from '@layout/payment-management-active/guards/is-payment-management-active.guard';
import { NgxsModule, provideStates } from '@ngxs/store';
import { adminSettingsRoutes } from '@pages/admin-settings/admin-settings.routes';
import { automationCenterState } from '@pages/automation-center/store';
import { cooperBotActivityLogState } from '@pages/cooper-bot-activity-log/store';
import { cooperBotState } from '@pages/cooper-bot/store';
import { cooperInsightsRoutes } from '@pages/cooper-insights/cooper-insights.routes';
import { cooperInsightsState } from '@pages/cooper-insights/store';
import { emailConfirmationState } from '@pages/email-confirmation/store';
import { executiveReportsOldRoutes } from '@pages/executive-reports-old/executive-reports-old.routes';
import { executiveReportOldStates } from '@pages/executive-reports-old/store';
import { executiveReportsRoutes } from '@pages/executive-reports/executive-reports.routes';
import { executiveReportStates } from '@pages/executive-reports/store';
import {
  hasFirstTimeOnboardingAccessGuard,
  isFirstTimeOnboardingCompletedGuard,
} from '@pages/first-time-onboarding/guards/first-time-onboarding.guard';
import { firstTimeOnboardingStates } from '@pages/first-time-onboarding/store';
import { homeState } from '@pages/home/store';
import { k365SetupGuidesStates } from '@pages/k365-setup-guides/store';
import { hasKaseyaStoreAccessGuard } from '@pages/kaseya-store/guards/has-kaseya-store-access.guard';
import { legalTermsArchiveState } from '@pages/legal-terms-archive/store';
import { loginRecoveryRoutes } from '@pages/login-recovery-options/routes/login-recovery.routes';
import { loginRecoveryState } from '@pages/login-recovery-options/store';
import { loginRoutes } from '@pages/login/login.routes';
import { loginState } from '@pages/login/store';
import { logoutState } from '@pages/logout/store';
import { organizationMappingState } from '@pages/organization-mapping/store';
import { isPartnerProgramActiveGuard } from '@pages/partner-program/guards/is-partner-program-active.guard';
import { partnerProgramRoutes } from '@pages/partner-program/partner-program.routes';
import { professionalServicesState } from '@pages/professional-services/store';
import { userModuleManagementState } from '@pages/profile-module-management/store';
import { profileSecurityState } from '@pages/profile-security/store';
import { profileState } from '@pages/profile/store';
import { pushMfaVerificationState } from '@pages/push-mfa-verification/store';
import { reportCardRoutes } from '@pages/report-card/report-card.routes';
import { reportCardPageState } from '@pages/report-card/store';
import { resetPasswordState } from '@pages/reset-password/store';
import { setPasswordState } from '@pages/set-password/store';
import { subscriptionsStateOld } from '@pages/subscriptions-old/store';
import { subscriptionsOldRoutes } from '@pages/subscriptions-old/subscriptions-old.routes';
import { subscriptionsState } from '@pages/subscriptions/store';
import { supportTicketsState } from '@pages/support/store';
import { talentFinderState } from '@pages/talent-finder/store';
import { hasTenantsGuard } from '@pages/tenants/guards/has-tenants.guard';
import { thirdPartySsoState } from '@pages/third-party-sso-authentication/store';
import { totpMfaVerificationState } from '@pages/totp-mfa-verification/store';
import {
  userIdentityVerificationPath,
  userIdentityVerificationRoutes,
} from '@pages/user-identity-verification/routes/user-identity-verification.routes';
import { userIdentityVerificationStates } from '@pages/user-identity-verification/store';
import { paymentManagementRoutes } from '@pages/payment-management/payment-management.routes';
import { paymentManagementState } from '@pages/payment-management/store';
import { adminSettingsViewPermissionsList } from '@core/permissions/constants/permissions.const';

export const routes: AppRoutes = [
  {
    path: '',
    component: MainViewComponent,
    canActivate: [isAuthorizedUserGuard, isFirstTimeOnboardingCompletedGuard],
    children: [
      {
        path: '',
        redirectTo: TopNavigationPath.Home,
        pathMatch: 'full',
        data: { prefetchTarget: TopNavigationPath.Home },
      },
      {
        path: TopNavigationPath.Home,
        canMatch: [isAuthorizedUserGuard],
        children: [
          {
            path: 'dashboard',
            loadComponent: () =>
              import('@pages/home/components/home-page/home-page.component').then(({ HomePageComponent }) => HomePageComponent),
            data: {
              fitsScreenWidth: true,
            },
            providers: [provideFeatureTranslations(TranslationFeature.Home), provideStates([...homeState])],
          },
          {
            path: 'kaseya-365-setup-guide',
            canMatch: [isNotMigratedUserGuard, hasPermissionGuard(Permission.OnboardingSubscriptionsView), hasSetupGuidesAccessGuard],
            loadComponent: () =>
              import('@pages/k365-setup-guides/components/k365-setup-guides-page/k365-setup-guides-page.component').then(
                ({ K365SetupGuidesPageComponent }) => K365SetupGuidesPageComponent
              ),
            data: {
              title: PageTitleTranslationKey.Kaseya365SetupGuide,
              disableMobileVersion: true,
            },
            providers: [provideStates(k365SetupGuidesStates), provideFeatureTranslations(TranslationFeature.Kaseya365SetupGuide)],
          },
          {
            path: 'executive-reports',
            canMatch: [hasPermissionGuard(Permission.ExecutiveReports), isFeatureEnabledGuard(Feature.ExecutiveReportsRefactoring, true)],
            loadComponent: () =>
              import('@pages/executive-reports/components/executive-reports-page-wrapper/executive-reports-page-wrapper.component').then(
                ({ ExecutiveReportsPageWrapperComponent }) => ExecutiveReportsPageWrapperComponent
              ),
            children: executiveReportsRoutes,
            data: {
              title: PageTitleTranslationKey.ExecutiveReports,
              disableMobileVersion: true,
            },
            providers: [provideFeatureTranslations(TranslationFeature.ExecutiveReports), provideStates([...executiveReportStates])],
          },
          {
            path: 'executive-reports',
            canMatch: [hasPermissionGuard(Permission.ExecutiveReports)],
            loadComponent: () =>
              import('@pages/executive-reports-old/components/executive-reports-page/executive-reports-page.component').then(
                ({ ExecutiveReportsPageComponent }) => ExecutiveReportsPageComponent
              ),
            children: executiveReportsOldRoutes,
            data: {
              title: PageTitleTranslationKey.ExecutiveReports,
              disableMobileVersion: true,
            },
            providers: [provideFeatureTranslations(TranslationFeature.ExecutiveReports), provideStates([...executiveReportOldStates])],
          },
          {
            path: 'support',
            canMatch: [hasPermissionGuard(Permission.Zendesk)],
            loadComponent: () =>
              import('@pages/support/components/support/support.component').then(({ SupportComponent }) => SupportComponent),
            data: {
              title: PageTitleTranslationKey.Support,
              disableMobileVersion: true,
            },
            providers: [provideStates(supportTicketsState), provideFeatureTranslations(TranslationFeature.Support)],
          },
          {
            path: 'payment-management',
            canMatch: [isNotMigratedUserGuard, hasPermissionGuard(Permission.PaymentManagementView), isPaymentManagementActiveGuard],
            loadComponent: () =>
              import('@pages/payment-management/components/payment-management-page/payment-management-page.component').then(
                ({ PaymentManagementPageComponent }) => PaymentManagementPageComponent
              ),
            data: {
              title: PageTitleTranslationKey.PaymentManagement,
              disableMobileVersion: true,
              fitsScreenWidth: true,
            },
            providers: [provideStates(paymentManagementState), provideFeatureTranslations(TranslationFeature.PaymentManagement)],
            children: paymentManagementRoutes,
          },
          {
            path: 'subscriptions',
            canMatch: [redirectMigratedUserGuard([TopNavigationPath.BillingAndSubscriptions, 'subscriptions'])],
            children: [],
          },
          {
            path: 'subscriptions',
            canMatch: [isNotMigratedUserGuard, hasPermissionGuard(Permission.BillingAndSubscriptions)],
            loadComponent: () =>
              import('@pages/subscriptions-old/components/subscriptions-page/subscriptions-page.component').then(
                ({ SubscriptionsOldPageComponent }) => SubscriptionsOldPageComponent
              ),
            data: {
              title: PageTitleTranslationKey.Subscriptions,
              disableMobileVersion: true,
            },
            children: subscriptionsOldRoutes,
            providers: [provideStates(subscriptionsStateOld), provideFeatureTranslations(TranslationFeature.SubscriptionsOld)],
          },
          {
            path: 'partner-program',
            canMatch: [isPartnerProgramActiveGuard],
            loadComponent: () =>
              import('@pages/partner-program/components/partner-program/partner-program.component').then(
                ({ PartnerProgramComponent }) => PartnerProgramComponent
              ),
            children: partnerProgramRoutes,
            data: {
              title: PageTitleTranslationKey.PartnerProgram,
              disableMobileVersion: true,
            },
            providers: [provideFeatureTranslations(TranslationFeature.PartnerProgram)],
          },
          {
            path: 'professional-services',
            canMatch: [isNotMigratedUserGuard, hasPermissionGuard(Permission.ProfessionalServicesDashboard)],
            loadComponent: () =>
              import('@pages/professional-services/components/professional-services-page/professional-services-page.component').then(
                ({ ProfessionalServicesPageComponent }) => ProfessionalServicesPageComponent
              ),
            data: {
              title: PageTitleTranslationKey.ProfessionalServices,
              disableMobileVersion: true,
            },
            providers: [provideStates(professionalServicesState), provideFeatureTranslations(TranslationFeature.ProfessionalServices)],
          },
          {
            path: 'report-card',
            canMatch: [hasPermissionGuard([Permission.AbsorbLearningSummary, Permission.AbsorbUserCourses])],
            loadComponent: () =>
              import('@pages/report-card/components/report-card-page/report-card-page.component').then(
                ({ ReportCardPageComponent }) => ReportCardPageComponent
              ),
            data: {
              disableMobileVersion: true,
              title: PageTitleTranslationKey.ReportCard,
            },
            children: reportCardRoutes,
            providers: [provideStates(reportCardPageState), provideFeatureTranslations(TranslationFeature.ReportCard)],
          },
          {
            path: '',
            redirectTo: redirectToFn(TopNavigationPath.Home, 'dashboard'),
            pathMatch: 'full',
            data: {
              prefetchTarget: 'dashboard',
            },
          },
        ],
      },
      {
        path: TopNavigationPath.Automations,
        canMatch: [isAuthorizedUserGuard],
        children: [
          {
            path: 'automation-center',
            canMatch: [hasPermissionGuard(Permission.AutomationCenterView)],
            loadComponent: () =>
              import('@pages/automation-center/components/automation-center-page/automation-center-page.component').then(
                ({ AutomationCenterPageComponent }) => AutomationCenterPageComponent
              ),
            data: {
              title: PageTitleTranslationKey.AutomationCenter,
              fitsScreenWidth: true,
            },
            providers: [provideFeatureTranslations(TranslationFeature.AutomationCenter), provideStates(automationCenterState)],
          },
          {
            path: 'cooper-insights',
            canMatch: [hasPermissionGuard(Permission.InsightsView)],
            loadComponent: () =>
              import('@pages/cooper-insights/components/cooper-insights-page/cooper-insights-page.component').then(
                ({ CooperInsightsPageComponent }) => CooperInsightsPageComponent
              ),
            providers: [provideStates(cooperInsightsState), provideFeatureTranslations(TranslationFeature.CooperInsights)],
            data: {
              title: PageTitleTranslationKey.CooperInsights,
              disableMobileVersion: true,
            },
            children: cooperInsightsRoutes,
          },
          {
            path: 'cooper-bots',
            canMatch: [hasPermissionGuard(Permission.CooperBotsView)],
            loadComponent: () =>
              import('@pages/cooper-bot/components/cooper-bot-page/cooper-bot-page.component').then(
                ({ CooperBotPageComponent }) => CooperBotPageComponent
              ),
            providers: [
              provideStates([...cooperBotState, ...cooperBotActivityLogState]),
              provideFeatureTranslations(TranslationFeature.CooperBots),
            ],
            data: {
              title: PageTitleTranslationKey.CooperBots,
              disableMobileVersion: true,
            },
          },
          {
            path: '',
            redirectTo: redirectToFn(TopNavigationPath.Automations, 'automation-center'),
            pathMatch: 'full',
            data: { prefetchTarget: 'automation-center' },
          },
        ],
      },
      {
        path: TopNavigationPath.Organizations,
        canMatch: [isAuthorizedUserGuard],
        children: [
          {
            path: 'executive-reports',
            canMatch: [hasPermissionGuard(Permission.ExecutiveReports), isFeatureEnabledGuard(Feature.ExecutiveReportsRefactoring, true)],
            loadComponent: () =>
              import('@pages/executive-reports/components/executive-reports-page-wrapper/executive-reports-page-wrapper.component').then(
                ({ ExecutiveReportsPageWrapperComponent }) => ExecutiveReportsPageWrapperComponent
              ),
            children: executiveReportsRoutes,
            data: {
              title: PageTitleTranslationKey.ExecutiveReports,
              disableMobileVersion: true,
            },
            providers: [provideFeatureTranslations(TranslationFeature.ExecutiveReports), provideStates([...executiveReportStates])],
          },
          {
            path: 'executive-reports',
            canMatch: [hasPermissionGuard(Permission.ExecutiveReports)],
            loadComponent: () =>
              import('@pages/executive-reports-old/components/executive-reports-page/executive-reports-page.component').then(
                ({ ExecutiveReportsPageComponent }) => ExecutiveReportsPageComponent
              ),
            children: executiveReportsOldRoutes,
            data: {
              title: PageTitleTranslationKey.ExecutiveReports,
              disableMobileVersion: true,
            },
            providers: [provideFeatureTranslations(TranslationFeature.ExecutiveReports), provideStates([...executiveReportOldStates])],
          },
          {
            path: 'organization-mapping',
            canMatch: [hasPermissionGuard(Permission.OrganizationMappingsView), isFeatureEnabledGuard(Feature.OrganizationMappings)],
            loadComponent: () =>
              import('@pages/organization-mapping/components/organization-mapping-page/organization-mapping-page.component').then(
                ({ OrganizationMappingPageComponent }) => OrganizationMappingPageComponent
              ),
            data: {
              title: PageTitleTranslationKey.OrganizationMapping,
              disableMobileVersion: true,
              fitsScreenWidth: true,
            },
            providers: [provideStates(organizationMappingState), provideFeatureTranslations(TranslationFeature.OrganizationMapping)],
          },
          {
            path: '',
            redirectTo: redirectToFn(TopNavigationPath.Organizations, 'executive-reports'),
            pathMatch: 'full',
            data: { prefetchTarget: 'executive-reports' },
          },
        ],
      },
      {
        path: TopNavigationPath.BillingAndSubscriptions,
        canMatch: [isAuthorizedUserGuard, isMigratedUserGuard],
        children: [
          {
            path: 'subscriptions',
            canMatch: [hasPermissionGuard(Permission.BillingAndSubscriptions)],
            loadComponent: () =>
              import('@pages/subscriptions/components/subscriptions-page/subscriptions-page.component').then(
                ({ SubscriptionsPageComponent }) => SubscriptionsPageComponent
              ),
            data: {
              title: PageTitleTranslationKey.Subscriptions,
              fitsScreenWidth: true,
            },
            providers: [provideStates(subscriptionsState), provideFeatureTranslations(TranslationFeature.Subscriptions)],
          },
          {
            path: 'payment-methods',
            canMatch: [isMigratedUserGuard, hasPermissionGuard(Permission.PaymentManagementView)],
            loadComponent: () =>
              import('@pages/payment-management/components/payment-management-page/payment-management-page.component').then(
                ({ PaymentManagementPageComponent }) => PaymentManagementPageComponent
              ),
            data: {
              title: PageTitleTranslationKey.PaymentManagement,
              disableMobileVersion: true,
              fitsScreenWidth: true,
            },
            providers: [provideStates(paymentManagementState), provideFeatureTranslations(TranslationFeature.PaymentManagement)],
            children: paymentManagementRoutes,
          },
          {
            path: '',
            redirectTo: redirectToFn(TopNavigationPath.BillingAndSubscriptions, 'subscriptions'),
            pathMatch: 'full',
            data: { prefetchTarget: 'subscriptions' },
          },
        ],
      },
      {
        path: TopNavigationPath.UserSettings,
        canMatch: [isAuthorizedUserGuard],
        children: [
          {
            path: 'profile',
            loadComponent: () =>
              import('@pages/profile/components/profile/profile.component').then(({ ProfileComponent }) => ProfileComponent),
            canMatch: [hasPermissionGuard(Permission.UserProfileRead)],
            data: {
              disableMobileVersion: true,
              title: PageTitleTranslationKey.Profile,
            },
            providers: [provideStates(profileState), provideFeatureTranslations(TranslationFeature.Profile)],
          },
          {
            path: 'module-management',
            canMatch: [hasPermissionGuard(Permission.LauncherModulesView)],
            loadComponent: () =>
              import('@pages/profile-module-management/components/profile-module-management-page/profile-module-management-page.component').then(
                ({ ProfileModuleManagementPageComponent }) => ProfileModuleManagementPageComponent
              ),
            data: {
              disableMobileVersion: true,
              title: PageTitleTranslationKey.ModuleManagement,
            },
            providers: [provideStates(userModuleManagementState), provideFeatureTranslations(TranslationFeature.ProfileModuleManagement)],
          },
          {
            path: 'security',
            canMatch: [hasPermissionGuard(Permission.PushNotifications)],
            loadComponent: () =>
              import('@pages/profile-security/components/profile-security/profile-security.component').then(
                ({ ProfileSecurityComponent }) => ProfileSecurityComponent
              ),
            data: {
              disableMobileVersion: true,
              title: PageTitleTranslationKey.Security,
            },
            providers: [provideFeatureTranslations(TranslationFeature.ProfileSecurity), provideStates(profileSecurityState)],
          },
          {
            path: '',
            redirectTo: redirectToFn(TopNavigationPath.UserSettings, 'profile'),
            pathMatch: 'full',
            data: { prefetchTarget: 'profile' },
          },
        ],
      },
      {
        path: TopNavigationPath.AdminSettings,
        canMatch: [isAuthorizedUserGuard, hasPermissionGuard(adminSettingsViewPermissionsList)],
        loadComponent: () =>
          import('@pages/admin-settings/components/admin-settings.component').then(({ AdminSettingsComponent }) => AdminSettingsComponent),
        children: adminSettingsRoutes,
        data: {
          title: PageTitleTranslationKey.AdminSettings,
        },
        providers: [provideFeatureTranslations(TranslationFeature.AdminSettings)],
      },
      {
        path: TopNavigationPath.LegalTermsArchive,
        canMatch: [hasPermissionGuard(Permission.LegalTermsArchiveView)],
        loadComponent: () =>
          import('@pages/legal-terms-archive/components/legal-terms-archive-page/legal-terms-archive-page.component').then(
            ({ LegalTermsArchivePageComponent }) => LegalTermsArchivePageComponent
          ),
        data: {
          title: PageTitleTranslationKey.LegalTermsArchive,
          hideLeftNavigation: true,
        },
        providers: [provideFeatureTranslations(TranslationFeature.LegalTermsArchive), provideStates([...legalTermsArchiveState])],
      },
      {
        path: 'talent-finder',
        canMatch: [isAuthorizedUserGuard, hasPermissionGuard(Permission.RemoteItSecurityManagementView)],
        loadComponent: () =>
          import('@pages/talent-finder/components/talent-finder-page/talent-finder-page.component').then(
            ({ TalentFinderPageComponent }) => TalentFinderPageComponent
          ),
        data: {
          title: PageTitleTranslationKey.TalentFinder,
          disableMobileVersion: true,
        },
        providers: [provideFeatureTranslations(TranslationFeature.TalentFinder), provideStates([...talentFinderState])],
      },
      {
        path: TopNavigationPath.KaseyaInsights,
        canMatch: [hasPermissionGuard(Permission.BusinessInsightsView), isFeatureEnabledGuard(Feature.BusinessInsights)],
        loadComponent: () =>
          import('@pages/business-insights/components/business-insights/business-insights.component').then(
            ({ BusinessInsightsComponent }) => BusinessInsightsComponent
          ),
        data: {
          fitsScreenWidth: true,
          hideLeftNavigation: true,
          hideAdaChat: true,
          disableMobileVersion: true,
          title: PageTitleTranslationKey.BusinessInsights,
        },
        children: [{ path: '**', children: [] }],
      },
    ],
  },
  {
    path: '',
    component: AuthViewComponent,
    children: [
      {
        path: ApplicationPath.Login,
        children: loginRoutes,
        providers: [provideStates([...loginState])],
        data: {
          title: PageTitleTranslationKey.Login,
        },
      },
      {
        path: 'connect',
        children: loginRoutes,
        data: {
          title: PageTitleTranslationKey.Login,
        },
        providers: [provideStates([...loginState])],
      },
      {
        path: ApplicationPath.CannotLogin,
        loadComponent: () =>
          import('@pages/login-recovery-options/components/login-recovery-options-page/login-recovery-options-page.component').then(
            ({ LoginRecoveryOptionsPageComponent }) => LoginRecoveryOptionsPageComponent
          ),
        providers: [provideStates(loginRecoveryState)],
        children: loginRecoveryRoutes,
        data: {
          title: PageTitleTranslationKey.CannotLogin,
        },
      },
      {
        path: 'product-access-denied',
        loadComponent: () => import('@pages/product-access-denied/components/product-access-denied/product-access-denied.component'),
        providers: [provideFeatureTranslations(TranslationFeature.ProductAccessDenied)],
        data: {
          title: PageTitleTranslationKey.ProductAccessDenied,
        },
      },
      {
        path: ApplicationPath.PushMfaVerification,
        canMatch: [hasLoginContextGuard],
        loadComponent: () =>
          import('@pages/push-mfa-verification/components/push-mfa-verification/push-mfa-verification.component').then(
            ({ PushMfaVerificationComponent }) => PushMfaVerificationComponent
          ),
        data: {
          title: PageTitleTranslationKey.PushMfa,
        },
        providers: [provideStates([...pushMfaVerificationState])],
      },
      {
        path: 'connect/tenants',
        canMatch: [hasTenantsGuard],
        loadComponent: () =>
          import('@pages/tenants/components/tenants-page/tenants-page.component').then(({ TenantsPageComponent }) => TenantsPageComponent),
        data: {
          title: PageTitleTranslationKey.Tenants,
        },
      },
      {
        path: ApplicationPath.Logout,
        data: {
          bypassLogin: true,
        },
        loadComponent: () => import('@pages/logout/components/logout.component').then(({ LogoutComponent }) => LogoutComponent),
        providers: [provideFeatureTranslations(TranslationFeature.Logout), importProvidersFrom(NgxsModule.forFeature([...logoutState]))],
      },
      {
        path: 'connect/logout',
        loadComponent: () => import('@pages/logout/components/logout.component').then(({ LogoutComponent }) => LogoutComponent),
        data: {
          workflow: PostLogoutWorkflow.OpenIdConnect,
          bypassLogin: true,
        },
        providers: [provideFeatureTranslations(TranslationFeature.Logout), importProvidersFrom(NgxsModule.forFeature([...logoutState]))],
      },
      {
        path: 'confirm-email/:token',
        loadComponent: () => import('@pages/email-confirmation/components/email-confirmation/email-confirmation.component'),
        providers: [
          provideFeatureTranslations(TranslationFeature.EmailConfirmation),
          importProvidersFrom(NgxsModule.forFeature([...emailConfirmationState])),
        ],
        data: {
          disableMobileVersion: true,
        },
      },
      {
        path: ':token/reset-password',
        loadComponent: () =>
          import('@pages/reset-password/components/reset-password-page/reset-password-page.component').then(
            ({ ResetPasswordPageComponent }) => ResetPasswordPageComponent
          ),
        providers: [provideStates([...resetPasswordState]), provideFeatureTranslations(TranslationFeature.ResetPassword)],
        data: {
          title: PageTitleTranslationKey.ResetPassword,
          bypassLogin: true,
        },
      },
      {
        path: 'user/sign-up/:token',
        loadComponent: () =>
          import('@pages/set-password/components/set-password-page/set-password-page.component').then(
            ({ SetPasswordPageComponent }) => SetPasswordPageComponent
          ),
        providers: [provideStates([...setPasswordState]), provideFeatureTranslations(TranslationFeature.SetPassword)],
        data: {
          title: PageTitleTranslationKey.SetPassword,
        },
      },
      {
        path: 'impersonate',
        loadComponent: () =>
          import('@pages/impersonate/components/impersonate.component').then(({ ImpersonateComponent }) => ImpersonateComponent),
        data: {
          title: PageTitleTranslationKey.Impersonate,
          disableMobileVersion: true,
        },
      },
      {
        path: 'third-party-sso',
        loadComponent: () =>
          import('@pages/third-party-sso-authentication/components/third-party-sso/third-party-sso.component').then(
            ({ ThirdPartySsoComponent }) => ThirdPartySsoComponent
          ),
        providers: [provideStates([...thirdPartySsoState, ...loginState])],
        data: {
          title: PageTitleTranslationKey.ThirdPartySso,
        },
      },
      {
        path: userIdentityVerificationPath,
        loadComponent: () =>
          import('@pages/user-identity-verification/components/user-identity-verification-page/user-identity-verification-page.component').then(
            ({ UserIdentityVerificationPageComponent }) => UserIdentityVerificationPageComponent
          ),
        providers: [provideStates(userIdentityVerificationStates), provideFeatureTranslations(TranslationFeature.UserIdentityVerification)],
        children: userIdentityVerificationRoutes,
        data: {
          title: PageTitleTranslationKey.UserIdentityVerification,
        },
      },
    ],
  },
  {
    path: ApplicationPath.TotpMfaVerification,
    canMatch: [hasLoginContextGuard],
    loadComponent: () =>
      import('@pages/totp-mfa-verification/components/totp-mfa-verification.component').then(
        ({ TotpMfaVerificationComponent }) => TotpMfaVerificationComponent
      ),
    providers: [provideStates([...totpMfaVerificationState])],
    data: {
      title: PageTitleTranslationKey.TotpMfa,
    },
  },
  {
    path: ApplicationPath.TotpMfaEnrollment,
    canMatch: [hasLoginContextGuard],
    loadComponent: () =>
      import('@pages/verification-setup/components/verification-setup/verification-setup.component').then(
        ({ VerificationSetupComponent }) => VerificationSetupComponent
      ),
    data: {
      title: PageTitleTranslationKey.TotpMfaEnrollment,
    },
  },
  {
    path: 'onboarding',
    canMatch: [
      isNotMigratedUserGuard,
      isAuthorizedUserGuard,
      hasPermissionGuard(Permission.OnboardingFirstAdminUserView),
      hasFirstTimeOnboardingAccessGuard,
    ],
    loadComponent: () =>
      import('@pages/first-time-onboarding/components/first-time-onboarding-page/first-time-onboarding-page.component').then(
        ({ FirstTimeOnboardingPageComponent }) => FirstTimeOnboardingPageComponent
      ),
    data: {
      title: PageTitleTranslationKey.FirstTimeOnboarding,
      disableMobileVersion: true,
    },
    providers: [provideStates(firstTimeOnboardingStates), provideFeatureTranslations(TranslationFeature.Onboarding)],
  },
  {
    path: 'pro-services',
    loadComponent: () =>
      import('@pages/registration/components/registration.component').then(({ RegistrationComponent }) => RegistrationComponent),
    data: {
      title: PageTitleTranslationKey.Registration,
    },
  },
  {
    path: 'helpdesk',
    loadComponent: () =>
      import('@pages/support-tickets-authentication/components/authentication-page/authentication-page.component').then(
        ({ AuthenticationPageComponent }) => AuthenticationPageComponent
      ),
  },
  {
    path: 'store',
    canActivate: [hasKaseyaStoreAccessGuard],
    component: AuthViewComponent,
    children: [
      {
        path: '',
        loadComponent: () =>
          import('@pages/kaseya-store/components/kaseya-store-page/kaseya-store-page.component').then(
            ({ KaseyaStorePageComponent }) => KaseyaStorePageComponent
          ),
        data: {
          title: PageTitleTranslationKey.KaseyaStore,
          disableMobileVersion: true,
        },
      },
    ],
  },
  {
    path: 'access-denied',
    loadComponent: () =>
      import('@pages/access-denied/components/access-denied.component').then(({ AccessDeniedComponent }) => AccessDeniedComponent),
    data: {
      title: PageTitleTranslationKey.AccessDenied,
    },
    providers: [provideFeatureTranslations(TranslationFeature.AccessDenied)],
  },
  {
    path: '**',
    redirectTo: 'home',
  },
];
